import { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { Lock, Unlock, Phone, ArrowUpRight, CheckCircle2, Loader2 } from 'lucide-react';
import MagneticButton from './MagneticButton';

const MATERIALS = [
    { id: 'papa', name: 'Papa Termozgrzewalna', pricePerSqm: 140, desc: 'Niezawodna hydroizolacja dachów płaskich.', popular: true },
    { id: 'blacha_rabek', name: 'Blacha płaska na rąbek', pricePerSqm: 250, desc: 'Nowoczesny i elegancki wygląd.', popular: false },
    { id: 'blachodachowka', name: 'Blachodachówka', pricePerSqm: 180, desc: 'Ekonomiczne i sprawdzone rozwiązanie.', popular: false },
];

export default function PricingEstimator() {
    const [area, setArea] = useState(150);
    const [material, setMaterial] = useState(MATERIALS[0]);
    const [phone, setPhone] = useState('');
    const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'unlocked'
    const [estimatedPrice, setEstimatedPrice] = useState({ min: 0, max: 0 });

    const compRef = useRef(null);
    const resultRef = useRef(null);

    // Initial GSAP Reveal
    useEffect(() => {
        let ctx = gsap.context(() => {
            gsap.from(".est-el", {
                scrollTrigger: {
                    trigger: compRef.current,
                    start: "top 80%",
                },
                y: 40,
                opacity: 0,
                stagger: 0.1,
                duration: 1,
                ease: "power3.out",
            });
        }, compRef);
        return () => ctx.revert();
    }, []);

    // Calculate Price Range
    useEffect(() => {
        // Base calculation includes material + basic labor estimate
        const baseCost = area * material.pricePerSqm;
        // Add some margin for variation (accessories, complexity)
        const minCost = Math.round((baseCost * 0.9) / 1000) * 1000; 
        const maxCost = Math.round((baseCost * 1.3) / 1000) * 1000;
        setEstimatedPrice({ min: minCost, max: maxCost });
    }, [area, material]);

    const handleUnlock = async (e) => {
        e.preventDefault();
        if (phone.length < 9) {
            alert("Podaj prawidłowy numer telefonu.");
            return;
        }

        setStatus('loading');

        // Webhook integration
        const scriptURL = 'https://script.google.com/macros/s/AKfycbyoDdavxXZzdT8-vuXHyELp-zIH6H1Rw7MFRkVuSkJRNnKDQaQpuC3RN1y2vCe8n70L/exec'; 
        const data = new FormData();
        data.append('Imię/Firma', 'Z ESTYMATORA');
        data.append('Telefon', phone);
        data.append('Wiadomość', `Dach: ${area}m2, Materiał: ${material.name}. Estymacja: ${estimatedPrice.min} - ${estimatedPrice.max} PLN`);

        try {
            await fetch(scriptURL, { 
                method: 'POST', 
                body: data,
                mode: 'no-cors' 
            });
            
            // Artificial delay to simulate processing and build anticipation
            setTimeout(() => {
                setStatus('unlocked');
                
                // Animate unlock
                gsap.fromTo(resultRef.current, 
                    { scale: 0.95, opacity: 0, y: 20 },
                    { scale: 1, opacity: 1, y: 0, duration: 0.8, ease: "back.out(1.5)" }
                );
            }, 1200);

        } catch (error) {
            console.error('Error!', error.message);
            setStatus('idle');
            alert("Coś poszło nie tak. Spróbuj formularza kontaktowego niżej.");
        }
    };

    return (
        <section ref={compRef} id="kalkulator" className="bg-primary py-24 px-6 md:px-16 text-dark border-t border-dark/10">
            <div className="max-w-5xl mx-auto">
                <div className="text-center mb-16 est-el">
                    <span className="font-mono text-sm text-accent uppercase tracking-widest block mb-4">
                        Szybka Estymacja
                    </span>
                    <h2 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-4 text-dark">
                        Estymator Wyceny Dachu
                    </h2>
                    <p className="font-body text-xl text-dark/70 max-w-2xl mx-auto">
                        Poznaj przybliżone koszty inwestycji bez czekania na audyt. Wybierz metraż, materiał i zdobądź błyskawiczną estymację.
                    </p>
                </div>

                <div className="bg-background rounded-[3rem] p-8 md:p-12 shadow-2xl border border-dark/10 est-el relative overflow-hidden">
                    {/* Noise Texture for the calculator card */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'}}></div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10">
                        {/* LEFT COLUMN: Inputs */}
                        <div className="space-y-10">
                            
                            {/* Area Slider */}
                            <div>
                                <div className="flex justify-between items-end mb-4">
                                    <label className="font-heading font-bold uppercase tracking-wider text-dark/80">
                                        Powierzchnia Dachu
                                    </label>
                                    <div className="font-mono font-bold text-3xl text-accent">
                                        {area} <span className="text-sm text-dark/40">m²</span>
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="50" 
                                    max="400" 
                                    step="5"
                                    value={area}
                                    onChange={(e) => setArea(Number(e.target.value))}
                                    className="w-full h-3 bg-dark/5 rounded-full appearance-none cursor-pointer focus:outline-none accent-accent hover:bg-dark/10 transition-colors"
                                    style={{
                                        background: `linear-gradient(to right, #E63B2E 0%, #E63B2E ${(area - 50) / 350 * 100}%, rgba(17,17,17,0.05) ${(area - 50) / 350 * 100}%, rgba(17,17,17,0.05) 100%)`
                                    }}
                                />
                                <div className="flex justify-between text-xs font-mono text-dark/40 mt-2">
                                    <span>50 m²</span>
                                    <span>400+ m²</span>
                                </div>
                            </div>

                            {/* Material Selection */}
                            <div>
                                <label className="font-heading font-bold uppercase tracking-wider text-dark/80 mb-4 block">
                                    Typ Pokrycia
                                </label>
                                <div className="space-y-3">
                                    {MATERIALS.map(mat => (
                                        <div 
                                            key={mat.id}
                                            onClick={() => setMaterial(mat)}
                                            className={`relative cursor-pointer transition-all duration-300 rounded-2xl p-5 border-2 flex items-center justify-between
                                                ${material.id === mat.id ? 'border-accent bg-accent/5 shadow-[0_0_20px_rgba(230,59,46,0.1)]' : 'border-dark/10 hover:border-dark/30 hover:bg-dark/5'}
                                            `}
                                        >
                                            {mat.popular && (
                                                <div className="absolute -top-3 right-4 bg-accent text-background text-[10px] font-mono font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                                                    Najpopularniejsze
                                                </div>
                                            )}
                                            <div>
                                                <h4 className="font-heading font-bold text-lg text-dark">{mat.name}</h4>
                                                <p className="font-mono text-xs text-dark/50 mt-1">{mat.desc}</p>
                                            </div>
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                                                ${material.id === mat.id ? 'border-accent bg-accent' : 'border-dark/20'}
                                            `}>
                                                {material.id === mat.id && <div className="w-2 h-2 bg-background rounded-full"></div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>

                        {/* RIGHT COLUMN: The Result / Lock Screen */}
                        <div className="flex flex-col">
                            <label className="font-heading font-bold uppercase tracking-wider text-dark/80 mb-4 block">
                                Szacowany Koszt
                            </label>

                            <div className="flex-grow flex flex-col justify-center bg-primary rounded-3xl p-8 border border-dark/10 relative overflow-hidden group">
                                
                                {status !== 'unlocked' ? (
                                    /* LOCKED STATE */
                                    <div className="text-center z-10 flex flex-col items-center">
                                        <div className="w-16 h-16 bg-background rounded-full shadow-lg flex items-center justify-center border border-dark/10 text-dark/50 mb-6 group-hover:scale-110 transition-transform duration-500">
                                            <Lock size={24} />
                                        </div>
                                        <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-dark mb-2">
                                            Wycena Zablokowana
                                        </h3>
                                        <p className="font-body text-sm text-dark/60 mb-8 max-w-[250px]">
                                            Podaj numer telefonu, aby natychmiast odblokować widełki cenowe dla wybranych parametrów.
                                        </p>
                                        
                                        <form onSubmit={handleUnlock} className="w-full max-w-[300px]">
                                            <div className="relative mb-4">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-dark/40">
                                                    <Phone size={18} />
                                                </div>
                                                <input 
                                                    type="tel" 
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    placeholder="Twój numer telefonu"
                                                    required
                                                    className="w-full bg-background border border-dark/10 rounded-xl pl-12 pr-4 py-4 text-dark font-body focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all text-sm"
                                                    disabled={status === 'loading'}
                                                />
                                            </div>
                                            <MagneticButton
                                                type="submit"
                                                disabled={status === 'loading'}
                                                className={`w-full font-heading font-bold text-sm px-6 py-4 rounded-xl uppercase tracking-widest flex items-center justify-center gap-2 transition-all duration-300
                                                    ${status === 'loading' ? 'bg-dark/10 text-dark/50 cursor-wait' : 'bg-accent text-background hover:bg-accent/90 shadow-[0_5px_20px_rgba(230,59,46,0.3)]'}
                                                `}
                                            >
                                                {status === 'loading' ? (
                                                    <><Loader2 size={18} className="animate-spin" /> Przetwarzanie...</>
                                                ) : (
                                                    <>Odblokuj Wycenę <Unlock size={18} /></>
                                                )}
                                            </MagneticButton>
                                        </form>
                                        <p className="font-mono text-[9px] text-dark/30 mt-4 uppercase tracking-widest">
                                            Bez spamu. Szybki kontakt.
                                        </p>
                                    </div>
                                ) : (
                                    /* UNLOCKED STATE */
                                    <div ref={resultRef} className="text-center z-10 flex flex-col items-center justify-center h-full">
                                        <div className="w-16 h-16 bg-[#4ADE80]/20 text-[#4ADE80] rounded-full flex items-center justify-center mb-6">
                                            <CheckCircle2 size={32} />
                                        </div>
                                        <span className="font-mono text-xs text-dark/50 uppercase tracking-widest mb-2">
                                            Przybliżona Inwestycja:
                                        </span>
                                        <div className="font-heading font-bold text-4xl lg:text-5xl text-dark tracking-tight leading-none mb-6">
                                            {estimatedPrice.min.toLocaleString('pl-PL')} <br className="hidden lg:block"/>
                                            <span className="text-2xl text-dark/50">-</span> <br className="hidden lg:block"/>
                                            {estimatedPrice.max.toLocaleString('pl-PL')} <span className="text-2xl text-accent">PLN</span>
                                        </div>
                                        <div className="bg-background/50 border border-dark/10 rounded-xl p-4 text-left w-full mt-auto">
                                            <p className="font-mono text-[10px] text-dark/60 uppercase leading-relaxed">
                                                * Powyższa kwota ma charakter orientacyjny i zakłada standardowy stopień skomplikowania dachu. 
                                                Nasz inżynier zadzwoni w ciągu 24h na numer <strong>{phone}</strong>, aby umówić darmową rewizję i podać ostateczną kwotę co do złotówki.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Abstract BG Graphic for Right Column */}
                                <div className="absolute -bottom-12 -right-12 text-dark/[0.03] pointer-events-none">
                                    <svg width="200" height="200" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </section>
    );
}
